import ClearIcon from '@mui/icons-material/Clear';
import { Box, ListItemText, MenuItem, Select, SelectChangeEvent, styled, useTheme } from '@mui/material';
import React from 'react';

import { IFilterOption } from '../Filters/Filters';

interface IProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: IFilterOption[];
  onClear: (onChange: (value: string) => void) => void;
}

interface IVariables {
  clearIconColor: string;
  hoverCleanIconColor: string;
}

const FilterWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius / 2,
  cursor: 'pointer',
  '&:hover .hover-overlay': {
    backgroundColor: theme.sass.hoverFilterBackground,
  },
}));

const HoverOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: theme.shape.borderRadius / 2,
  zIndex: 0,
  transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  position: 'relative',
  fontSize: 16,
  zIndex: 1,

  '& .MuiSelect-standard': {
    paddingLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius / 2,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  '& .MuiSelect-icon': {
    color: 'gray',
    marginRight: 0,
  },
}));

const ClearIconWrapper = styled(Box)({
  position: 'absolute',
  right: 24,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
});

const StyledClearIcon = styled(ClearIcon, {
  shouldForwardProp: (prop) => prop !== 'variables',
})<{ variables: IVariables }>(({ variables }) => ({
  fontSize: 20,
  color: variables.clearIconColor,
  cursor: 'pointer',
  '&:hover': {
    color: variables.hoverCleanIconColor,
  },
}));

const StyledMenuItem = styled(MenuItem)<{ selected: boolean }>(({ theme, selected }) => ({
  borderLeft: '3px solid',
  borderColor: selected ? theme.palette.primary.main : 'transparent',
}));

const FilterSelect: React.FC<IProps> = ({ label, value, onChange, options, onClear }) => {
  const { sass: variables } = useTheme();

  const iconVariables: IVariables = {
    clearIconColor: variables.clearIconColor,
    hoverCleanIconColor: variables.hoverCleanIconColor,
  };

  const renderDisplayLabel = React.useCallback(() => {
    if (value) {
      const selectedOption = options.find((opt) => opt.value === value);
      return selectedOption?.label || label;
    }
    return label;
  }, [value, options, label]);

  return (
    <FilterWrapper>
      <HoverOverlay className="hover-overlay" />
      <StyledSelect
        value={value}
        onChange={(event: SelectChangeEvent<unknown>) => onChange(event.target.value as string)}
        displayEmpty
        renderValue={renderDisplayLabel}
        disableUnderline
        variant="standard"
        MenuProps={{
          anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom',
          },
          transformOrigin: { horizontal: 'left', vertical: 'top' },
          PaperProps: {
            sx: {
              minWidth: `200px !important`,
              maxWidth: 230,
              maxHeight: 200,
              '& .MuiTypography-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              },
            },
          },
        }}
        sx={{
          color: value ? 'primary.main' : 'text.primary',
          '& .MuiSelect-standard': {
            paddingRight: value ? '48px !important' : 32,
          },
        }}
      >
        {options.map((option) => {
          const { value: optionValue, label: optionLabel } = option;
          return (
            <StyledMenuItem key={optionValue} value={optionValue} selected={value === optionValue}>
              <ListItemText primary={optionLabel} />
            </StyledMenuItem>
          );
        })}
      </StyledSelect>
      {!!value && (
        <ClearIconWrapper
          onClick={(e) => {
            e.stopPropagation();
            onClear(onChange);
          }}
        >
          <StyledClearIcon variables={iconVariables} />
        </ClearIconWrapper>
      )}
    </FilterWrapper>
  );
};

export default FilterSelect;
